import { X } from '@phosphor-icons/react';
import { useRef, useState } from 'react';
import type { WidgetEvent } from '../lib/chat-api';
import { cn } from '@/lib/utils';
import { Dialog, DialogContent, DialogClose } from '@/components/ui/dialog';

export interface McqAnswer {
  selectedIndices: number[];
  selectedLabels: string[];
  freeText?: string;
}

interface Props {
  widget: WidgetEvent;
  disabled?: boolean;
  onAnswer: (result: McqAnswer) => void;
  onDismiss?: () => void;
}

const optionBase = "text-left px-4 py-3 rounded-sm border-[1.5px] border-naia-muted-2 bg-naia-info-bg text-foreground font-bold text-[14px] cursor-pointer font-[inherit] min-h-[44px] disabled:cursor-default";

export default function ChatMcqModal({ widget, disabled, onAnswer, onDismiss }: Props) {
  const args = (widget.args ?? {}) as {
    question?: string;
    options?: string[];
    allowMultiple?: boolean;
    title?: string;
  };
  const options = args.options ?? [];
  const multi = !!args.allowMultiple;
  const [sel, setSel] = useState<number[]>([]);
  const [freeText, setFreeText] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const pick = (i: number) => {
    if (disabled) return;
    if (!multi) {
      onAnswer({ selectedIndices: [i], selectedLabels: [options[i] ?? ''], label: options[i] ?? '' } as McqAnswer & { label: string });
      return;
    }
    setSel((s) => (s.includes(i) ? s.filter((x) => x !== i) : [...s, i]));
  };

  const submitMulti = () => {
    if (!sel.length || disabled) return;
    onAnswer({ selectedIndices: sel, selectedLabels: sel.map((i) => options[i] ?? '') });
  };

  const submitFree = () => {
    const t = freeText.trim();
    if (!t || disabled) return;
    onAnswer({ selectedIndices: [], selectedLabels: [], freeText: t });
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onDismiss?.(); }}>
      <DialogContent showCloseButton={false} style={{ maxWidth: 480 }} className="gap-4 p-[24px_20px_20px]">
        <div className="flex items-start gap-3">
          <div className="flex-1 min-w-0 flex flex-col gap-1">
            {args.title && <div className="text-[10px] font-black tracking-[1.2px] text-naia-blue uppercase">{args.title}</div>}
            <div className="text-[16px] font-extrabold text-foreground leading-[1.4]">{args.question}</div>
          </div>
          {onDismiss && (
            <DialogClose aria-label="Fermer" className="shrink-0" style={{ marginRight: -8, marginTop: -8 }}>
              <X size={20} weight="bold" />
            </DialogClose>
          )}
        </div>

        <div className="flex flex-col gap-2">
          {options.map((opt, i) => {
            const on = sel.includes(i);
            return (
              <button
                key={i}
                type="button"
                onClick={() => pick(i)}
                disabled={disabled}
                className={cn(optionBase, on && "border-[2px] border-naia-blue")}
              >
                {opt}
              </button>
            );
          })}
        </div>

        {multi && sel.length > 0 && !disabled && (
          <button type="button" onClick={submitMulti} className="px-4 py-3 rounded-sm border-none bg-naia-blue text-card font-extrabold text-[14px] cursor-pointer font-[inherit] min-h-[44px]">
            Valider
          </button>
        )}

        <div className="flex gap-2 items-center border-t border-border pt-3">
          <input
            ref={inputRef}
            value={freeText}
            onChange={(e) => setFreeText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') submitFree(); }}
            placeholder="Autre réponse…"
            disabled={disabled}
            className="flex-1 px-[14px] py-[10px] border border-border rounded-sm text-[14px] font-[inherit] bg-naia-card-alt text-foreground outline-none min-h-[44px] box-border"
          />
          {freeText.trim() && !disabled && (
            <button type="button" onClick={submitFree} className="flex-none w-[44px] h-[44px] p-0 grid place-items-center bg-naia-blue text-card border-none rounded-full cursor-pointer text-[18px] font-[inherit]">
              ↑
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
